/**
 * Unified Document Viewer Page
 *
 * Handles all document viewing modes:
 * - view: Read-only view of document and extracted data
 * - review: Annotate, chat, and resolve issues (only for container documents)
 * - manager: File versions, classification, and actions
 *
 * URL: /shipper/containers/{containerId}/documents/{documentId}?mode=view|review|manager
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loading02, ChevronDown, ChevronUp, CheckCircle, RefreshCw01, Zap, Edit05, XCircle } from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';

import { Button } from '@/components/base/buttons/button';
import { PdfViewer } from '@/components/application/pdf-viewer/pdf-viewer';
import { PolygonManager } from '@/components/application/pdf-viewer/polygon-manager';
import { AnnotationDrawer } from '@/components/application/pdf-viewer/annotation-drawer';
import { DocumentSidebar } from '@/components/application/documents/document-sidebar';
import { DocumentViewerLayout } from '@/components/application/documents/document-viewer-layout';
import { ShareDocumentModal } from '@/components/application/documents/share-document-modal';
import type { DocumentViewMode } from '@/components/application/documents/document-mode-selector';

import { useShipmentById } from '@/hooks/api/use-containers-recap';
import { useContainerDocuments, useDocument, useValidateDocument, useUnvalidateDocument, useDocumentBlobUrl, useDocumentExtraction, useDocumentParsings, useExtractDocument, useUpdateDocument, useUpdateExtractionField, useSelectParsing, useExtractionStrategies } from '@/hooks/api/use-documents';
import { useDocumentTypes } from '@/hooks/api/use-document-types';
import { useDocumentSchemas } from '@/hooks/api/use-document-schemas';
import { useSchemaAttributes } from '@/hooks/api/use-schema-attributes';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Download01 } from '@untitledui/icons';
import { useDocumentPolygons } from '@/hooks/use-document-polygons';
import { toast } from '@/lib/toast-service';
import { ShipperOnboardingHeaderAction } from '@/components/application/onboarding/shipper-onboarding-context';
import { markShipperDocumentViewedForOnboarding } from '@/lib/shipper-onboarding-storage';

import type { ReviewPolygon } from '@/types/document-review';

// Format a field key for display: snake_case → "Snake Case", camelCase → "Camel Case"
function formatFieldKey(key: string): string {
  // Handle snake_case
  if (key.includes('_')) {
    return key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }
  // Handle camelCase
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
}

// Eve-names for extraction strategies
const STRATEGY_DISPLAY_NAMES: Record<string, string> = {
  vision: 'EveRead Vision',
  olmo_ocr_claude: 'EveRead OCR',
  schema_driven: 'EveRead Schema',
  deepseek: 'EveRead DeepScan',
};

// Image MIME types supported by browsers
const IMAGE_MIME_TYPES = new Set([
  'image/png', 'image/jpeg', 'image/jpg', 'image/gif',
  'image/webp', 'image/svg+xml', 'image/bmp', 'image/tiff',
]);

// Image file extensions
const IMAGE_EXTENSIONS = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'tif',
]);

// Excel file extensions (backend converts these to PNG via thumbnail endpoint)
const EXCEL_EXTENSIONS = new Set(['xlsx', 'xls']);

// Check if a document is an image (not a PDF)
function isImageFile(doc: { file_mime_type?: string | null; file_name?: string | null } | undefined): boolean {
  if (!doc) return false;
  if (doc.file_mime_type && IMAGE_MIME_TYPES.has(doc.file_mime_type.toLowerCase())) return true;
  const ext = doc.file_name?.split('.').pop()?.toLowerCase();
  return ext ? IMAGE_EXTENSIONS.has(ext) : false;
}

// Check if a document is an Excel file (rendered as image via backend conversion)
function isExcelFile(doc: { file_name?: string | null } | undefined): boolean {
  if (!doc) return false;
  const ext = doc.file_name?.split('.').pop()?.toLowerCase();
  return ext ? EXCEL_EXTENSIONS.has(ext) : false;
}

// Format a field value for display
function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return value || '-';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map(v => formatFieldValue(v)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

interface EnhancedField {
  key: string;
  label: string;
  value: string;
  importance: number; // 1=critical, 2=important, 3=optional
  isRequired: boolean;
  isMissing: boolean;
  isManuallyEdited: boolean;
  helpText: string | null;
  displayOrder: number;
}

/** Inline editable input for a missing schema field */
function MissingFieldInput({
  field,
  documentId,
  mutation,
  importanceDot,
}: {
  field: EnhancedField;
  documentId: string;
  mutation: ReturnType<typeof useUpdateExtractionField>;
  importanceDot: (importance: number) => ReactNode;
}) {
  const [value, setValue] = useState('');
  const isSaving = mutation.isPending && mutation.variables?.fieldKey === field.key;

  const handleSave = () => {
    if (!value.trim() || !documentId) return;
    mutation.mutate(
      { documentId, fieldKey: field.key, fieldValue: value.trim() },
      { onSuccess: () => setValue('') },
    );
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-tertiary flex items-center gap-1.5">
        {importanceDot(field.importance)}
        {field.label}
        {field.isRequired && (
          <span className="text-[9px] font-medium text-error-600 bg-error-50 px-1 py-0.5 rounded">Required</span>
        )}
      </label>
      <div className="flex gap-1.5">
        <input
          type="text"
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
          placeholder={field.helpText || `Enter ${field.label.toLowerCase()}...`}
          className="flex-1 rounded-md border border-dashed border-gray-300 bg-transparent px-3 py-2 text-sm text-primary placeholder:text-quaternary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 min-h-[36px]"
        />
        {value.trim() && (
          <Button
            size="sm"
            color="primary"
            onClick={handleSave}
            disabled={isSaving}
            className="shrink-0"
          >
            {isSaving ? '...' : 'Save'}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Editable schema field — click to edit, save/cancel, clear to move to missing */
function EditableSchemaField({
  field,
  documentId,
  mutation,
  importanceDot,
  hasSchema,
}: {
  field: EnhancedField;
  documentId: string;
  mutation: ReturnType<typeof useUpdateExtractionField>;
  importanceDot: (importance: number) => ReactNode;
  hasSchema: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(field.value);
  const isSaving = mutation.isPending && mutation.variables?.fieldKey === field.key;

  const handleSave = () => {
    if (!documentId) return;
    const trimmed = editValue.trim();
    // Save empty string to clear the field (moves it to missing)
    mutation.mutate(
      { documentId, fieldKey: field.key, fieldValue: trimmed || '' },
      { onSuccess: () => setEditing(false) },
    );
  };

  const handleCancel = () => {
    setEditValue(field.value);
    setEditing(false);
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-tertiary flex items-center gap-1.5">
        {hasSchema && importanceDot(field.importance)}
        {field.label}
        <span className="inline-block size-1.5 rounded-full bg-success-500 shrink-0" />
        {field.isManuallyEdited && (
          <Edit05 className="size-3 text-tertiary" />
        )}
      </label>
      {editing ? (
        <div className="flex gap-1.5">
          <input
            type="text"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') handleCancel();
            }}
            autoFocus
            className="flex-1 rounded-md border border-brand-300 bg-transparent px-3 py-2 text-sm text-primary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 min-h-[36px]"
          />
          <Button size="sm" color="primary" onClick={handleSave} disabled={isSaving} className="shrink-0">
            {isSaving ? '...' : 'Save'}
          </Button>
          <Button size="sm" color="secondary" onClick={handleCancel} className="shrink-0">
            Cancel
          </Button>
        </div>
      ) : (
        <div
          className="rounded-md bg-secondary px-3 py-2 text-sm text-primary min-h-[36px] cursor-pointer hover:ring-1 hover:ring-gray-300 transition-shadow"
          onClick={() => { setEditValue(field.value); setEditing(true); }}
        >
          {field.value}
        </div>
      )}
    </div>
  );
}

export default function UnifiedDocumentViewerPage() {
  const { t } = useTranslation(['pages:documents', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { containerId, documentId } = useParams<{ containerId: string; documentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    markShipperDocumentViewedForOnboarding();
  }, []);

  // Get mode from URL, default to 'view'
  const mode = (searchParams.get('mode') as DocumentViewMode) || 'view';

  // State
  const [currentPage, setCurrentPage] = useState(1);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showExtractionDetails, setShowExtractionDetails] = useState(false);
  const [showParsingsHistory, setShowParsingsHistory] = useState(false);

  // Manager mode editing state
  const [editingName, setEditingName] = useState<string | null>(null);
  const [_editingType, setEditingType] = useState<string | null>(null);

  // Fetch container data
  const { data: containerData } = useShipmentById(containerId);
  const containerNumber = containerData?.container_number || containerData?.booking_number || containerData?.bl_number || undefined;

  // Fetch documents — container docs if available, otherwise fetch individual doc
  const { data: documents = [] } = useContainerDocuments(containerId);
  const { data: standaloneDoc } = useDocument(documentId || '');
  const currentDocument = (containerId
    ? documents.find(d => d.document_id === documentId)
    : undefined) ?? standaloneDoc ?? undefined;

  // Fetch PDF
  const { data: pdfBlobUrl, isLoading: pdfLoading, error: pdfError } = useDocumentBlobUrl(documentId);

  // Extraction data (for view + manager modes)
  const { data: extractionData, isLoading: extractionLoading, refetch: refetchExtraction } = useDocumentExtraction(documentId || '');
  const { data: parsingsData, isLoading: parsingsLoading, refetch: refetchParsings } = useDocumentParsings(documentId || '');
  const extractMutation = useExtractDocument();
  const updateDocumentMutation = useUpdateDocument();
  const selectParsingMutation = useSelectParsing();
  const { data: documentTypesData } = useDocumentTypes();
  const { data: extractionStrategies } = useExtractionStrategies();
  const [selectedStrategy, setSelectedStrategy] = useState<string>('vision');

  // Resolve document_type_id: use FK if present, otherwise match legacy enum against document_types
  const resolvedDocTypeId = useMemo(() => {
    if (currentDocument?.document_type_id) return currentDocument.document_type_id;
    if (!currentDocument?.document_type || !documentTypesData) return null;
    const legacyType = currentDocument.document_type.toLowerCase();
    const match = documentTypesData.find(dt =>
      dt.key === legacyType ||
      dt.classifier_keys?.includes(legacyType)
    );
    return match?.document_type_id || null;
  }, [currentDocument?.document_type_id, currentDocument?.document_type, documentTypesData]);

  // Schema + attributes for extracted data display
  // Prefer document_type_id (points to document_types table) over legacy enum
  const docTypeId = currentDocument?.document_type_id;
  const docType = currentDocument?.document_type?.toLowerCase();
  const { data: schemasData } = useDocumentSchemas(
    docTypeId
      ? { document_type_id: docTypeId, is_active: true }
      : docType ? { document_type: docType, is_active: true } : undefined,
    { enabled: !!(docTypeId || docType) },
  );
  const activeSchema = schemasData?.find(s => s.is_default) || schemasData?.[0];
  const { data: schemaAttrsData } = useSchemaAttributes(activeSchema?.document_schema_id || '');

  // Polygons (for review mode)
  const {
    polygons,
    selectedPolygon,
    createPolygon,
    updatePolygon,
    deletePolygon,
    selectPolygon,
    setSelectedPolygon
  } = useDocumentPolygons(documentId);

  // Validate mutation
  const validateMutation = useValidateDocument();
  const unvalidateMutation = useUnvalidateDocument();

  // Build extracted data fields split by schema-mapped, missing, and other
  const hasSchema = !!activeSchema && !!schemaAttrsData;
  const { schemaFields, missingFields, otherFields } = useMemo(() => {
    let rawData = extractionData?.extracted_data as Record<string, unknown> | null | undefined;

    // Flatten key_attributes/additional_data nested structure from schema-driven extraction
    if (rawData && (rawData.key_attributes || rawData.additional_data)) {
      const flattened: Record<string, unknown> = {};
      if (typeof rawData.key_attributes === 'object' && rawData.key_attributes)
        Object.assign(flattened, rawData.key_attributes as Record<string, unknown>);
      if (typeof rawData.additional_data === 'object' && rawData.additional_data)
        Object.assign(flattened, rawData.additional_data as Record<string, unknown>);
      for (const [k, v] of Object.entries(rawData)) {
        if (k !== 'key_attributes' && k !== 'additional_data') flattened[k] = v;
      }
      rawData = flattened;
    }

    const data = rawData ?? {};
    const keys = Object.keys(data);

    // Build set of manually edited keys from audit trail
    const corrections = Array.isArray(data._manual_corrections) ? data._manual_corrections as Array<{ key: string }> : [];
    const manualKeys = new Set(corrections.map(c => c.key));

    if (!schemaAttrsData || schemaAttrsData.length === 0) {
      return {
        schemaFields: [] as EnhancedField[],
        missingFields: [] as EnhancedField[],
        otherFields: keys
          .filter(k => !k.startsWith('_'))
          .filter(k => {
            const v = data[k];
            return v !== null && v !== undefined && v !== '' && !(typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);
          })
          .sort()
          .map(key => ({
            key,
            label: formatFieldKey(key),
            value: formatFieldValue(data[key]),
            importance: 3,
            isRequired: false,
            isMissing: false,
            isManuallyEdited: manualKeys.has(key),
            helpText: null,
            displayOrder: 0,
          })),
      };
    }

    const schemaKeySet = new Set(schemaAttrsData.map(a => a.attribute_key).filter(Boolean));
    const matched: EnhancedField[] = [];
    const missing: EnhancedField[] = [];

    for (const a of schemaAttrsData) {
      if (!a.attribute_key) continue;
      const hasValue = keys.includes(a.attribute_key) &&
        data[a.attribute_key] !== null &&
        data[a.attribute_key] !== undefined &&
        data[a.attribute_key] !== '';
      const field: EnhancedField = {
        key: a.attribute_key,
        label: a.custom_label || a.attribute_label || formatFieldKey(a.attribute_key),
        value: hasValue ? formatFieldValue(data[a.attribute_key]) : '',
        importance: a.importance_level ?? 3,
        isRequired: a.is_required ?? false,
        isMissing: !hasValue,
        isManuallyEdited: manualKeys.has(a.attribute_key),
        helpText: a.custom_help_text ?? null,
        displayOrder: a.display_order ?? 999,
      };
      if (hasValue) matched.push(field);
      else missing.push(field);
    }

    // Other fields: in extraction but not in schema, filter out _ prefixed (audit) and empty values
    const other = keys
      .filter(k => !schemaKeySet.has(k) && !k.startsWith('_'))
      .filter(k => {
        const v = data[k];
        return v !== null && v !== undefined && v !== '' && !(typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);
      })
      .sort()
      .map(key => ({
        key,
        label: formatFieldKey(key),
        value: formatFieldValue(data[key]),
        importance: 3 as const,
        isRequired: false,
        isMissing: false,
        isManuallyEdited: manualKeys.has(key),
        helpText: null,
        displayOrder: 999,
      }));

    return { schemaFields: matched, missingFields: missing, otherFields: other };
  }, [extractionData, schemaAttrsData]);

  // Mutation for manual field entry
  const updateFieldMutation = useUpdateExtractionField();

  // Available modes - review only for container documents
  const availableModes: DocumentViewMode[] = containerId
    ? ['view', 'review', 'manager']
    : ['view', 'manager'];

  // Handle mode change
  const handleModeChange = useCallback((newMode: DocumentViewMode) => {
    setSearchParams({ mode: newMode });
  }, [setSearchParams]);

  // Handle document switch
  const handleSwitchDocument = useCallback((newDocumentId: string) => {
    if (newDocumentId !== documentId) {
      navigate(`/shipper/containers/${containerId}/documents/${newDocumentId}?mode=${mode}`);
    }
  }, [containerId, documentId, mode, navigate]);

  // Handle validate
  const handleValidate = async () => {
    if (!documentId) return;
    try {
      await validateMutation.mutateAsync(documentId);
      toast.success(t('review.validated', 'Document validated successfully'));
    } catch {
      toast.error(t('review.validateError', 'Failed to validate document'));
    }
  };

  const handleUnvalidate = async () => {
    if (!documentId) return;
    try {
      await unvalidateMutation.mutateAsync(documentId);
      toast.success('Validation removed');
    } catch {
      toast.error('Failed to remove validation');
    }
  };

  // Handle polygon creation
  const handleCreatePolygon = useCallback((options?: { pageNumber?: number; x?: number; y?: number }) => {
    if (!documentId) return;
    createPolygon({
      pageNumber: options?.pageNumber ?? currentPage,
      x: options?.x ?? 0.416,
      y: options?.y ?? 0.464,
      width: 0.168,
      height: 0.071,
      notes: '',
      attributes: {},
      comments: [],
    });
  }, [documentId, currentPage, createPolygon]);

  // Handle polygon update
  const handleUpdatePolygon = useCallback((polygonId: string, updates: Partial<ReviewPolygon>) => {
    updatePolygon(polygonId, updates);
  }, [updatePolygon]);

  // Handle polygon selection with page navigation
  const handleSelectPolygon = useCallback((polygonId: string) => {
    const polygon = polygons.find(p => p.id === polygonId);
    if (polygon) {
      selectPolygon(polygonId);
      requestAnimationFrame(() => {
        setCurrentPage(polygon.pageNumber);
        setTimeout(() => selectPolygon(polygonId), 100);
      });
    }
  }, [polygons, selectPolygon]);

  // Handle mark resolved
  const handleMarkResolved = useCallback((id: string, resolved: boolean) => {
    const polygon = polygons.find(p => p.id === id);
    if (!polygon) return;
    updatePolygon(id, {
      attributes: { ...polygon.attributes, resolved: String(resolved) }
    });
  }, [polygons, updatePolygon]);

  // Render the PDF viewer content
  const renderViewer = () => {
    if (pdfLoading) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-secondary/50 z-20">
          <Loading02 className="size-8 text-brand-600 animate-spin" />
        </div>
      );
    }

    if (pdfError) {
      const errorMessage = pdfError instanceof Error ? pdfError.message : 'Unknown error';
      const isExtractionFailed = currentDocument?.extraction_status === 'failed';

      return (
        <div className="absolute inset-0 flex items-center justify-center bg-secondary/50 z-20">
          <div className="text-center max-w-md px-4">
            <AlertCircle className="size-12 text-error-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-primary mb-2">
              {t('viewer.error.title', { defaultValue: 'Failed to load document' })}
            </h3>
            <p className="text-sm text-tertiary mb-2">
              {errorMessage}
            </p>
            {isExtractionFailed && (
              <p className="text-sm text-error-600 mb-4">
                {t('viewer.error.extractionFailed', { defaultValue: 'Document extraction failed. The file may be corrupted or in an unsupported format.' })}
              </p>
            )}
            <div className="flex gap-2 justify-center">
              <Button
                size="sm"
                color="primary"
                iconLeading={RefreshCw01}
                onClick={async () => {
                  // Force refetch by invalidating and refetching
                  await queryClient.invalidateQueries({ queryKey: ['document-blob', documentId] });
                  await queryClient.refetchQueries({ queryKey: ['document-blob', documentId] });
                }}
                disabled={pdfLoading}
              >
                {t('viewer.error.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (!pdfBlobUrl) return null;

    // Wait for document metadata to determine file type
    if (!currentDocument) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-secondary/50 z-20">
          <Loading02 className="size-8 text-brand-600 animate-spin" />
        </div>
      );
    }

    // Image files — render with <img> instead of PdfViewer
    if (isImageFile(currentDocument) || isExcelFile(currentDocument)) {
      return (
        <div className="w-full h-full flex items-start justify-center overflow-auto pt-4 px-4 pb-4">
          <img
            src={pdfBlobUrl}
            alt={currentDocument?.file_name || 'Document'}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      );
    }

    // View mode - no polygons/overlay
    if (mode === 'view') {
      return (
        <PdfViewer
          key={`${documentId}-view`}
          fileUrl={pdfBlobUrl}
          polygons={[]}
          hideAddRectangle={true}
          showZoomControls={true}
          initialScale={0.8}
        />
      );
    }

    // Review mode - with polygon overlay, zoom controls but no add rectangle button (moved to drawer)
    if (mode === 'review') {
      return (
        <PdfViewer
          key={`${documentId}-review`}
          fileUrl={pdfBlobUrl}
          polygons={polygons}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          hideAddRectangle={true}
          showZoomControls={true}
          initialScale={0.8}
          renderOverlay={({ pageNumber, pageWidth, pageHeight }) => (
            <PolygonManager
              pageNumber={pageNumber}
              pageWidth={pageWidth}
              pageHeight={pageHeight}
              polygons={polygons}
              selectedPolygonId={selectedPolygon?.id || null}
              onChange={updatePolygon}
              onSelect={selectPolygon}
              onDelete={deletePolygon}
            />
          )}
        />
      );
    }

    // Manager mode - just viewer (similar to view)
    return (
      <PdfViewer
        key={`${documentId}-manager`}
        fileUrl={pdfBlobUrl}
        polygons={[]}
        hideAddRectangle={true}
        showZoomControls={true}
        initialScale={0.8}
      />
    );
  };

  // Render right panel based on mode
  const renderRightPanel = () => {
    // View mode - extracted data
    if (mode === 'view') {
      const importanceDot = (importance: number) => {
        const color = importance === 1 ? 'bg-error-500' : importance === 2 ? 'bg-warning-400' : 'bg-gray-300';
        return <span className={`inline-block size-1.5 rounded-full ${color} shrink-0 mt-1`} />;
      };

      const renderOtherFieldList = (fields: EnhancedField[]) => (
        <div className="space-y-3">
          {fields.map(({ key, label, value, importance }) => (
            <div key={key} className="flex flex-col gap-1">
              <label className="text-xs text-tertiary flex items-center gap-1.5">
                {hasSchema && importanceDot(importance)}
                {label}
              </label>
              <div className="rounded-md bg-secondary px-3 py-2 text-sm text-primary min-h-[36px]">
                {value}
              </div>
            </div>
          ))}
        </div>
      );

      const totalSchemaFields = schemaFields.length + missingFields.length;
      const blockingMissing = missingFields.filter(f => f.isRequired || f.importance <= 2);
      const criticalCount = blockingMissing.filter(f => f.isRequired || f.importance === 1).length;
      const importantCount = blockingMissing.length - criticalCount;

      const isValidated = currentDocument?.validation_status === 'human_validated';
      const isValidating = validateMutation.isPending || unvalidateMutation.isPending;

      const handleValidateClick = () => {
        if (isValidated) {
          handleUnvalidate();
          return;
        }
        if (blockingMissing.length > 0) {
          const parts: string[] = [];
          if (criticalCount > 0) parts.push(`${criticalCount} required`);
          if (importantCount > 0) parts.push(`${importantCount} important`);
          toast.warning(`Note: ${parts.join(' and ')} field${blockingMissing.length > 1 ? 's' : ''} still missing`);
        }
        handleValidate();
      };

      return (
        <div className="p-4 flex-1 overflow-y-auto bg-primary">
          {/* Header with completion count */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-primary">
              {t('viewer.extractedData', { defaultValue: 'Extracted Data' })}
            </h3>
            {hasSchema && totalSchemaFields > 0 && (
              <span className="text-xs text-tertiary">
                {schemaFields.length}/{totalSchemaFields}
              </span>
            )}
          </div>

          {/* Validate / Invalidate toggle button */}
          {currentDocument?.extraction_status === 'extracted' && (
            <Button
              size="sm"
              color={isValidated ? 'primary-destructive' : 'secondary'}
              className="w-full mb-4"
              iconLeading={isValidated ? XCircle : CheckCircle}
              onClick={handleValidateClick}
              disabled={isValidating}
            >
              {isValidating
                ? (isValidated ? 'Invalidating...' : 'Validating...')
                : (isValidated ? 'Invalidate' : 'Validate')}
            </Button>
          )}

          {extractionLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loading02 className="size-5 text-brand-600 animate-spin" />
            </div>
          ) : schemaFields.length === 0 && missingFields.length === 0 && otherFields.length === 0 ? (
            <p className="text-sm text-tertiary text-center py-8">
              {t('viewer.noExtractionData', { defaultValue: 'No extraction data available' })}
            </p>
          ) : (
            <>
              {/* Schema-matched fields — editable */}
              {hasSchema && schemaFields.length > 0 && (
                <div className="space-y-3">
                  {schemaFields.map(field => (
                    <EditableSchemaField
                      key={field.key}
                      field={field}
                      documentId={documentId || ''}
                      mutation={updateFieldMutation}
                      importanceDot={importanceDot}
                      hasSchema={hasSchema}
                    />
                  ))}
                </div>
              )}
              {hasSchema && schemaFields.length === 0 && missingFields.length === 0 && otherFields.length > 0 && (
                <p className="text-xs text-tertiary mb-3">No matched schema attributes</p>
              )}
              {!hasSchema && !extractionLoading && docType && (
                <p className="text-xs text-tertiary mb-3">No schema available for this document type</p>
              )}

              {/* Missing fields section */}
              {hasSchema && missingFields.length > 0 && (
                <div className={schemaFields.length > 0 ? 'mt-5' : ''}>
                  <div className="flex items-center gap-2 mb-3">
                    <h4 className="text-xs font-medium text-tertiary">Missing Fields</h4>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${criticalCount > 0 ? 'bg-error-100 text-error-700' : 'bg-gray-100 text-gray-600'}`}>
                      {missingFields.length}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {missingFields.map(field => (
                      <MissingFieldInput
                        key={field.key}
                        field={field}
                        documentId={documentId || ''}
                        mutation={updateFieldMutation}
                        importanceDot={importanceDot}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Other fields in collapsible toggle */}
              {otherFields.length > 0 && (
                <details className={hasSchema && (schemaFields.length > 0 || missingFields.length > 0) ? 'mt-4' : ''}>
                  <summary className="text-xs font-medium text-tertiary cursor-pointer select-none hover:text-secondary py-1">
                    All Extracted Data ({otherFields.length} fields)
                  </summary>
                  <div className="mt-3">
                    {renderOtherFieldList(otherFields)}
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      );
    }

    // Review mode - annotation drawer
    if (mode === 'review') {
      const reviewIsValidated = currentDocument?.validation_status === 'human_validated';
      const reviewIsValidating = validateMutation.isPending || unvalidateMutation.isPending;

      return (
        <AnnotationDrawer
          isOpen={true}
          polygon={selectedPolygon}
          allPolygons={polygons}
          documentId={documentId || ''}
          onClose={() => setSelectedPolygon(null)}
          onUpdate={handleUpdatePolygon}
          onSelectPolygon={handleSelectPolygon}
          onDelete={deletePolygon}
          onCreatePolygon={() => handleCreatePolygon({ pageNumber: currentPage })}
          onMarkResolved={handleMarkResolved}
          isValidated={reviewIsValidated}
          onValidate={!reviewIsValidated ? handleValidate : undefined}
          onInvalidate={reviewIsValidated ? handleUnvalidate : undefined}
          onShare={() => setIsShareModalOpen(true)}
          isValidating={reviewIsValidating}
        />
      );
    }

    // Manager mode - editable fields and actions
    if (mode === 'manager') {
      const handleSaveName = () => {
        if (documentId && editingName !== null) {
          updateDocumentMutation.mutate({
            documentId,
            data: { file_name: editingName }
          }, {
            onSuccess: () => {
              toast.success('Document name updated');
              setEditingName(null);
            },
            onError: () => {
              toast.error('Failed to update name');
            }
          });
        }
      };

      const handleSaveType = (newType: string) => {
        if (documentId && newType) {
          updateDocumentMutation.mutate({
            documentId,
            data: { document_type_id: newType }
          }, {
            onSuccess: () => {
              toast.success('Document type updated');
              setEditingType(null);
            },
            onError: () => {
              toast.error('Failed to update type');
            }
          });
        }
      };

      const handleSelectParsing = (parsingId: string) => {
        if (!documentId) return;
        selectParsingMutation.mutate(
          { documentId, parsingId },
          {
            onSuccess: () => {
              toast.success('Parsing version selected');
            },
            onError: () => {
              toast.error('Failed to select parsing version');
            },
          },
        );
      };

      const handleDownload = async () => {
        if (!pdfBlobUrl) return;
        const link = document.createElement('a');
        link.href = pdfBlobUrl;
        link.download = currentDocument?.file_name || 'document.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      return (
        <div className="p-3 flex-1 overflow-y-auto bg-primary">
          <h3 className="text-sm font-medium text-primary mb-3">
            {t('viewer.documentManager', { defaultValue: 'Document Manager' })}
          </h3>
          <div className="space-y-3">
            {/* Editable Document Name */}
            <div className="space-y-1">
              <label className="text-[10px] text-tertiary">Document Name</label>
              {editingName !== null ? (
                <div className="flex gap-1">
                  <Input
                    value={editingName}
                    onChange={setEditingName}
                    size="sm"
                    className="flex-1"
                  />
                  <Button size="sm" onClick={handleSaveName} disabled={updateDocumentMutation.isPending}>
                    Save
                  </Button>
                  <Button size="sm" color="secondary" onClick={() => setEditingName(null)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div
                  className="text-xs text-primary p-2 rounded bg-secondary cursor-pointer hover:bg-secondary/80"
                  onClick={() => setEditingName(currentDocument?.file_name || '')}
                >
                  {currentDocument?.file_name || 'Unnamed'}
                </div>
              )}
            </div>

            {/* Editable Document Type — uses resolvedDocTypeId for legacy fallback */}
            <div className="space-y-1">
              <label className="text-[10px] text-tertiary">Document Type</label>
              <Select.ComboBox
                selectedKey={resolvedDocTypeId}
                onSelectionChange={(key) => { if (key) handleSaveType(key as string); }}
                items={documentTypesData?.map(dt => ({ id: dt.document_type_id, label: dt.name })) || []}
                size="sm"
                placeholder="Search document type..."
                shortcut={false}
              >
                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
              </Select.ComboBox>
            </div>

            {/* Extraction Status */}
            <div className="flex items-center justify-between p-2 rounded bg-secondary">
              <span className="text-[10px] text-tertiary">Extraction Status</span>
              <Badge
                size="sm"
                color={
                  currentDocument?.extraction_status === 'extracted' ? 'success' :
                    currentDocument?.extraction_status === 'failed' ? 'error' :
                      currentDocument?.extraction_status === 'processing' ? 'blue' :
                        'gray'
                }
              >
                {currentDocument?.extraction_status || 'pending'}
              </Badge>
            </div>

            {/* Extracted Data Collapsible */}
            <div className="border border-secondary rounded-lg overflow-hidden">
              <button
                onClick={() => setShowExtractionDetails(!showExtractionDetails)}
                className="w-full p-2.5 flex items-center justify-between bg-secondary hover:bg-primary_hover transition-colors"
              >
                <span className="text-xs font-medium text-primary flex items-center gap-1.5">
                  <CheckCircle className="size-3.5 text-success-600" />
                  Extracted Data
                </span>
                {showExtractionDetails ? <ChevronUp className="size-4 text-tertiary" /> : <ChevronDown className="size-4 text-tertiary" />}
              </button>
              {showExtractionDetails && (
                <div className="p-2.5 max-h-64 overflow-y-auto">
                  {extractionLoading ? (
                    <div className="text-center py-3">
                      <Loading02 className="size-4 animate-spin mx-auto text-brand-600" />
                    </div>
                  ) : extractionData?.extracted_data ? (
                    <div className="space-y-1.5">
                      {Object.entries(extractionData.extracted_data)
                        .filter(([key]) => !key.startsWith('_'))
                        .map(([key, value]) => (
                        <div key={key} className="flex justify-between gap-2 py-1 text-xs border-b border-secondary/50 last:border-0">
                          <span className="text-tertiary capitalize shrink-0">{key.replace(/_/g, ' ')}</span>
                          <span className="text-primary text-right truncate">
                            {typeof value === 'object' ? JSON.stringify(value) : String(value || '-')}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-tertiary text-center py-3">No data</p>
                  )}
                </div>
              )}
            </div>

            {/* Parsing History — expandable, with version selection */}
            <div className="border border-secondary rounded-lg overflow-hidden">
              <button
                onClick={() => setShowParsingsHistory(!showParsingsHistory)}
                className="w-full p-2.5 flex items-center justify-between bg-secondary hover:bg-primary_hover transition-colors"
              >
                <span className="text-xs font-medium text-primary flex items-center gap-1.5">
                  <RefreshCw01 className="size-3.5 text-brand-600" />
                  Parsing History ({parsingsData?.length || 0})
                </span>
                {showParsingsHistory ? <ChevronUp className="size-4 text-tertiary" /> : <ChevronDown className="size-4 text-tertiary" />}
              </button>
              {showParsingsHistory && (
                <div className="p-2.5">
                  {parsingsLoading ? (
                    <div className="text-center py-3">
                      <Loading02 className="size-4 animate-spin mx-auto text-brand-600" />
                    </div>
                  ) : parsingsData && parsingsData.length > 0 ? (
                    <div className="space-y-2.5">
                      {parsingsData.map((parsing, index) => {
                        const isActive = parsing.is_selected;
                        const isCompleted = parsing.parse_status === 'completed';
                        const isFailed = parsing.parse_status === 'failed';
                        const isSelecting = selectParsingMutation.isPending && selectParsingMutation.variables?.parsingId === parsing.parsing_id;

                        return (
                          <div
                            key={parsing.parsing_id}
                            className={`rounded-lg p-3 ${isActive ? 'bg-success-50/10 ring-1 ring-success-500' : 'bg-secondary'}`}
                          >
                            {/* Row 1: index + strategy | status badge */}
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-sm font-medium text-primary">
                                #{parsingsData.length - index} · {STRATEGY_DISPLAY_NAMES[parsing.parse_strategy || 'vision'] || parsing.parse_strategy || 'vision'}
                              </span>
                              <Badge
                                size="sm"
                                color={isCompleted ? 'success' : isFailed ? 'error' : 'gray'}
                              >
                                {parsing.parse_status}
                              </Badge>
                            </div>

                            {/* Field completeness bars */}
                            {isCompleted && parsing.parse_content && (() => {
                              // Flatten key_attributes/additional_data nesting
                              let rawData = parsing.parse_content as Record<string, unknown>;
                              if (rawData.key_attributes || rawData.additional_data) {
                                const flat: Record<string, unknown> = {};
                                if (typeof rawData.key_attributes === 'object' && rawData.key_attributes)
                                  Object.assign(flat, rawData.key_attributes as Record<string, unknown>);
                                if (typeof rawData.additional_data === 'object' && rawData.additional_data)
                                  Object.assign(flat, rawData.additional_data as Record<string, unknown>);
                                for (const [k, v] of Object.entries(rawData)) {
                                  if (k !== 'key_attributes' && k !== 'additional_data') flat[k] = v;
                                }
                                rawData = flat;
                              }

                              const hasVal = (v: unknown) => v !== null && v !== undefined && v !== '';

                              // Schema-based completeness
                              const allAttrs = schemaAttrsData?.filter(a => a.attribute_key) || [];
                              let allTotal: number, allFilled: number;
                              if (allAttrs.length > 0) {
                                allTotal = allAttrs.length;
                                allFilled = allAttrs.filter(a => hasVal(rawData[a.attribute_key!])).length;
                              } else {
                                // No schema — fall back to raw field count
                                const rawEntries = Object.entries(rawData).filter(([k]) => !k.startsWith('_'));
                                allTotal = rawEntries.length;
                                allFilled = rawEntries.filter(([, v]) => hasVal(v)).length;
                              }
                              const allPct = allTotal > 0 ? Math.round((allFilled / allTotal) * 100) : 0;

                              // Critical fields completeness (required or importance 1)
                              const criticalAttrs = allAttrs.filter(a => a.is_required || a.importance_level === 1);
                              const critTotal = criticalAttrs.length;
                              const critFilled = criticalAttrs.filter(a => hasVal(rawData[a.attribute_key!])).length;
                              const critPct = critTotal > 0 ? Math.round((critFilled / critTotal) * 100) : 0;

                              return (
                                <div className="space-y-1 mb-1.5">
                                  {/* Critical fields */}
                                  {critTotal > 0 && (
                                    <div className="flex items-center gap-2" title={`Critical: ${critFilled} of ${critTotal} required fields`}>
                                      <span className="text-[10px] text-tertiary w-11 shrink-0">Critical</span>
                                      <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                                        <div
                                          className={`h-full rounded-full ${critPct === 100 ? 'bg-success-500' : critPct >= 50 ? 'bg-warning-400' : 'bg-error-400'}`}
                                          style={{ width: `${critPct}%` }}
                                        />
                                      </div>
                                      <span className="text-[10px] text-tertiary tabular-nums w-7 text-right">
                                        {critFilled}/{critTotal}
                                      </span>
                                    </div>
                                  )}
                                  {/* All fields */}
                                  <div className="flex items-center gap-2" title={`All fields: ${allFilled} of ${allTotal} extracted`}>
                                    <span className="text-[10px] text-tertiary w-11 shrink-0">{critTotal > 0 ? 'All' : 'Fields'}</span>
                                    <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                                      <div
                                        className={`h-full rounded-full ${allPct >= 70 ? 'bg-success-500' : allPct >= 40 ? 'bg-warning-400' : 'bg-error-400'}`}
                                        style={{ width: `${allPct}%` }}
                                      />
                                    </div>
                                    <span className="text-[10px] text-tertiary tabular-nums w-7 text-right">
                                      {allFilled}/{allTotal}
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Row 2: date */}
                            <div className="text-xs text-tertiary">
                              {new Date(parsing.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })},{' '}
                              {new Date(parsing.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                            </div>

                            {/* Error message for failed parsings */}
                            {isFailed && parsing.error_message && (
                              <p className="mt-2 text-xs text-error-500 line-clamp-2">
                                {parsing.error_message}
                              </p>
                            )}

                            {/* Action row */}
                            <div className="mt-2.5">
                              {isActive ? (
                                <Badge size="sm" color="success">Active</Badge>
                              ) : isCompleted ? (
                                <Button
                                  size="sm"
                                  color="secondary"
                                  className="w-full"
                                  onClick={() => handleSelectParsing(parsing.parsing_id)}
                                  disabled={isSelecting}
                                >
                                  {isSelecting ? 'Selecting...' : 'Use this version'}
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-tertiary text-center py-3">No history</p>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="pt-2 border-t border-secondary space-y-1.5">
              {/* Strategy selector */}
              <div className="space-y-1">
                <label className="text-[10px] text-tertiary">Extraction Strategy</label>
                <Select
                  selectedKey={selectedStrategy}
                  onSelectionChange={(key) => { if (key) setSelectedStrategy(key as string); }}
                  items={(extractionStrategies || []).map(s => ({
                    id: s.name,
                    label: STRATEGY_DISPLAY_NAMES[s.name] || s.name,
                  }))}
                  size="sm"
                  placeholder="Select strategy..."
                >
                  {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                </Select>
              </div>
              <Button
                color="primary"
                size="sm"
                className="w-full"
                iconLeading={Zap}
                onClick={() => {
                  if (documentId) {
                    extractMutation.mutate({
                      documentId,
                      request: { strategy: selectedStrategy, force_reextract: true }
                    }, {
                      onSuccess: () => {
                        toast.success('Extraction started');
                        refetchExtraction();
                        refetchParsings();
                      },
                      onError: (err) => {
                        toast.error(`Extraction failed: ${err.message}`);
                      }
                    });
                  }
                }}
                disabled={extractMutation.isPending}
              >
                {extractMutation.isPending ? 'Extracting...' : 'Re-extract Data'}
              </Button>
              <Button
                color="secondary"
                size="sm"
                className="w-full"
                iconLeading={Download01}
                onClick={handleDownload}
                disabled={!pdfBlobUrl}
              >
                Download
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderHeaderActions = () => {
    return <ShipperOnboardingHeaderAction />;
  };

  return (
    <>
      <DocumentViewerLayout
        mode={mode}
        onModeChange={handleModeChange}
        availableModes={availableModes}
        documentId={documentId || ''}
        documentName={currentDocument?.file_name ?? undefined}
        containerId={containerId}
        containerNumber={containerNumber}
        sidebar={
          <DocumentSidebar
            documents={documents}
            selectedDocumentId={documentId}
            onSelectDocument={handleSwitchDocument}
            showValidationStatus={true}
            showCategory={true}
            showFormat={true}
          />
        }
        viewer={renderViewer()}
        rightPanel={renderRightPanel()}
        headerActions={renderHeaderActions()}
        showBackButton={true}
        showQuickNavigation={true}
      />
      <ShareDocumentModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        documentId={documentId || ''}
      />
    </>
  );
}
